/* [PATCH pomoc-fe v2] Vsebina pomoči — aplikacija Nabava.
   Vir napisov: recon v1, v8, v10 (15.10.2026) iz kode na testu (DokumentiPage, PlacilaPage, NabavaPregledPage, NastavitvePage, SifrantiPage).
   Pravilo: samo napisi, ki obstajajo v kodi. Ob spremembi zaslona posodobi tudi to datoteko. */
import { Postopek, StranPomoci, Tezava } from '../tipi';

const jePrejeti = (pot: string, sp: URLSearchParams) => pot === '/dokumenti' && !(sp.get('tip') || '').startsWith('izdani');

/* ======================= PREJETI RAČUNI ======================= */

const P_UVOZ: Postopek = {
  id: 'nabava.uvoz', naslov: 'Uvoz prejetih računov', za: 'vsi',
  koraki: [
    'Meni **Prejeti računi**.',
    '**Uvoz** (modri gumb desno zgoraj) → okno **Uvoz računov**: izberi datoteke — **PDF ali Word (.doc, .docx)**, lahko več naenkrat. Okno lahko zapreš, obdelava teče naprej v ozadju.',
    '**Posamični uvoz** → okno **Naloži dokument**: **PDF, JPG, PNG, eSlog XML**.',
    'Gumb **Uvoz iz ePredala** (če je prikazan) prevzame eRačune iz bizBox ePredala.',
    'Uvoženi računi se pojavijo v seznamu; sistem prebere podatke in predlaga kontiranje.',
  ],
};

const P_SEZNAM: Postopek = {
  id: 'nabava.seznam', naslov: 'Seznam prejetih računov', za: 'vsi',
  koraki: [
    'Zgoraj izbereš leto in filter **Vsi**, **Neprenešeni**, **Nepotrjeni**; iskanje je po partnerju, znesku, postavkah in kontih.',
    'Oznake: **Potrjen** (potrjeno s strani računovodstva), **Plačano**, **Likvidiran** / **Nelikvidiran**, **DVOJNIK** (račun s to številko je za tega dobavitelja že v bazi), **NI V ŠIFRANTU**.',
    'Klik na račun ga odpre v maski desno, s **PDF Predogled**.',
  ],
};

const P_LIKVIDACIJA: Postopek = {
  id: 'nabava.likvidacija', naslov: 'Likvidacija in opomba za računovodstvo', za: 'vsi',
  koraki: [
    'Če je likvidacija vklopljena, račun pred potrditvijo in plačilom potrebuje likvidacijo — dokler ni likvidiran, sta **potrditev in plačilo zaprta**.',
    'Odpri račun v maski in ga likvidiraj z gumbom za likvidacijo; likvidacijo lahko prekličeš, da račun spet urejaš.',
    'Opombo za računovodstvo vpiši v polje opombe (npr. »račun se nanaša na …«) in klikni **Shrani opombo**. Tudi ko je račun zaklenjen, opombo lahko še dopolniš.',
  ],
};

const P_UREJANJE_STRANKA: Postopek = {
  id: 'nabava.urejanje', naslov: 'Urejanje in brisanje prejetega računa', za: 'vsi',
  koraki: [
    'Dokler račun ni potrjen, lahko popraviš glavo (številka, datumi, partner) in spremembe shraniš s **Shrani** (**Esc = Prekliči**).',
    'Nepotrjen račun, ki si ga naložil sam, izbrišeš z **Izbriši dokument** (okno **Brisanje dokumenta**). Dejanja ni mogoče razveljaviti.',
    'Potrjenega računa ne moreš več urejati — to ureja računovodstvo.',
  ],
};

const P_OBDELAVA_R: Postopek = {
  id: 'nabava.obdelava-r', naslov: 'Obdelava prejetega računa', za: 'racunovodja',
  koraki: [
    'V maski preveri številko računa, interno številko (klik za urejanje), tip dokumenta, dobavitelja (podatke partnerja urediš v šifrantu), TRR in sklic.',
    'Postavke (**Naziv**, **Kol.**, **Cena**, **DDV**, **Vrednost**, **Konto**); ikona **Opisi za konto (pravila razbitja)**.',
    '**Datumi**, **Kontiranje** in **DDV Evidenca — KPR**.',
    'Devizni račun: valuta, znesek v valuti in datum tečaja → **Preračunaj v EUR**. Postavke izven DDV: **+ ostalo (neobdavčeno)**.',
    'Spremembe shrani s **Shrani**, račun potrdi s **Potrdi**. Potrditev se prekliče v oknu **Preklic potrditve**.',
    'Za stranko lahko pustiš opombo (nit opomb na dnu maske) — stranka jo vidi kot »za urediti«.',
  ],
};

const P_RAZDELI_R: Postopek = {
  id: 'nabava.razdeli-r', naslov: 'Razdeli, združi, veži', za: 'racunovodja',
  koraki: [
    '**Razdeli dokument**: ikona **Razdeli PDF na več računov** v maski.',
    '**Združi dokumente**: ikona **Ta dokument je del drugega — združi ju v enega**.',
    '**+ Veži dobropis / avans** → **Veži dobropis / avans na račun**: izberi dokument, **Znesek pobota (€)** → **Poveži**.',
  ],
};

const P_NEDAVNI_R: Postopek = {
  id: 'nabava.nedavni-r', naslov: 'Nedavni uvozi in razveljavitev', za: 'racunovodja',
  koraki: [
    '**Nedavni uvozi** → **Nedavni uvozi šopov**: **Preštevilči** ali **Razveljavi**.',
    'Ob **Razveljavitev je blokirana** dokumente **Označi kot neprenešeno** in **Poskusi znova**.',
    'Okno **Davčna blokada zaostaja** ponudi zaklep obdobja: **Zakleni do vključno (MM.YYYY)**.',
  ],
};

/* ======================= PLAČILA ======================= */

const P_PLACILA: Postopek = {
  id: 'nabava.placila', naslov: 'Plačila dobaviteljem', za: 'vsi',
  koraki: [
    'Meni **Plačila** — seznam odprtih prejetih računov za plačilo.',
    'Filtri: **Iskanje po vseh poljih**, **IBAN plačnika**, **Rok od** / **Rok do**, **Dobavitelj**, **Odprto od €** / **Odprto do €**, **Interna od** / **Interna do**, **Datum plačil**.',
    'Pri računu vidiš **IBAN / Sklic**, **Plačilo** in **Pripravljeno plačilo**; pripravljeno plačilo **Popravi** ali **Izbriši**.',
    'Račun, ki ga stranka še ni likvidirala, je označen (»Stranka računa še ni likvidirala«).',
  ],
};

const P_SEPA: Postopek = {
  id: 'nabava.sepa', naslov: 'SEPA paket in UPN QR', za: 'vsi',
  koraki: [
    'Za pripravljena plačila odpri okno **SEPA paket**: **Samo prenesi paket** (datoteko uvoziš v banko) ali **Označi za plačane**.',
    '**UPN QR** pokaže **Plačilo s QR kodo** — skeniraj z aplikacijo mobilne banke, nato **Plačano**.',
    'Ročno plačilo: **Znesek €**, **Datum plačila** → **Shrani**.',
    'Ikona **Zgodovina plačil** pokaže pretekla plačila računa.',
  ],
};

/* ======================= DRUGO ======================= */

const P_PREGLED: Postopek = {
  id: 'nabava.pregled', naslov: 'Pregled nabave', za: 'vsi',
  koraki: [
    'Meni **Pregled** — prejeti računi s **Številka**, **Interna**, **Dobavitelj**, **Osnova**, **Strošek**, **Skupaj**, **Odprto**, **Stanje**.',
    'Primerjavo izbereš: **isto obdobje lani**, **prejšnje obdobje** ali **brez primerjave**.',
  ],
};

const P_PRENOS_R: Postopek = {
  id: 'nabava.prenos-r', naslov: 'Prenos v računovodski program', za: 'racunovodja',
  koraki: [
    'Stran **Prenosi**. Program (**Vasco (KN-21)** ali **Opal / Pantheon — VOD**) je iz Nastavitve → Računovodski program; smer prenosa določa izbrana aplikacija.',
    'Zavihka **Za prenos** in **Preneseno**; iskanje z **Interna št. od**, **Datum od**, **Iskanje (št./partner)**.',
    'Po želji vključi vgradnjo PDF v XML (VOD: Sken, KN-21: Priloga).',
    'Označi dokumente → **Prenesi izbrane**; **Odznači izbrane**.',
  ],
};

const P_SIFRANTI: Postopek = {
  id: 'nabava.sifranti', naslov: 'Šifranti', za: 'vsi',
  koraki: [
    '**Partnerji**: iskanje po imenu, davčni, ID za DDV; **Uredi**. Neaktiven partner se v izbirnikih ne ponuja.',
    '**Stroškovna mesta** in **Projekti**: **Šifra**, **Naziv** (stranka jih samo vidi).',
  ],
};

const P_SIFRANTI_R: Postopek = {
  id: 'nabava.sifranti-r', naslov: 'Kontni plan, opisi za konte, vrste knjige DDV', za: 'racunovodja',
  koraki: [
    '**Kontni plan**: **Koda**, **Naziv**, **Zahteva SM**, **Privzeti SM**, **Saldakonto**.',
    '**Opisi za konte** (zavihka **Prejeti** / **Izdani**): **Beseda**, **Konto (končni, ≥4 mesta)**, **Pravilo določa**, **Partner (neobvezno — iz šifranta)**. Ročno vneseno pravilo ima prednost pred učnimi vzorci.',
    '**Vrste knjige DDV** (samo v Nabavi): **Šifra**, **Naziv**, **Lastnosti**.',
  ],
};

const P_NASTAVITVE_R: Postopek = {
  id: 'nabava.nastavitve-r', naslov: 'Nastavitve Nabave', za: 'racunovodja',
  koraki: [
    '**Splošne nastavitve**: bizBox ePredal, **Blokada davčnega obdobja**, **Zaklep poslovnega leta**, **Številčenje partnerjev**, **Uvoz novih partnerjev preko XML**, **Priloga PDF pri prenosih**.',
    'Zavihki prejetih: številčenje (predpone pred interno številko), kontiranje (konti za knjiženje DDV po stopnjah in vrstah), ostalo (konto za plačilo prek plačnika, **Likvidacija računov**).',
  ],
};

/* ======================= težave ======================= */

const T_LIKV: Tezava = { vprasanje: 'Računa ni mogoče potrditi ali plačati', odgovor: 'Račun še ni likvidiran — potrditev in plačilo sta do likvidacije zaprta.', za: 'vsi' };
const T_ZAKLENJEN: Tezava = { vprasanje: 'Računa ne morem urejati', odgovor: 'Račun je potrjen in zaklenjen; opombo za računovodstvo lahko še vedno dopolniš.', za: 'vsi' };
const T_DVOJNIK: Tezava = { vprasanje: 'Oznaka DVOJNIK', odgovor: 'Račun s to številko je za tega dobavitelja že v bazi. Preveri, ali gre za isti račun.', za: 'vsi' };
const T_POTRDI_R: Tezava = { vprasanje: '»Potrditev ni mogoča«', odgovor: 'Sporočilo pove, kaj manjka (npr. vrsta DDV v vrstici ali napaka v glavi). Popravi in znova klikni **Potrdi**.', za: 'racunovodja' };

/* ======================= strani ======================= */

export const NABAVA: StranPomoci[] = [
  {
    kljuc: 'nabava.pregled', naslov: 'Pregled nabave', za: 'vsi',
    ujema: (pot) => pot === '/nabava-pregled',
    opis: 'Pregled prejetih računov z zneski, odprtimi postavkami in stanjem.',
    postopki: [P_PREGLED, P_UVOZ],
  },
  {
    kljuc: 'nabava.prejeti', naslov: 'Prejeti računi', za: 'vsi',
    ujema: jePrejeti,
    opis: 'Prejeti računi, dobropisi in avansi. Zgoraj desno so gumbi za uvoz; klik na račun ga odpre v maski.',
    postopki: [P_UVOZ, P_SEZNAM, P_LIKVIDACIJA, P_UREJANJE_STRANKA, P_OBDELAVA_R, P_RAZDELI_R, P_NEDAVNI_R],
    tezave: [T_LIKV, T_ZAKLENJEN, T_DVOJNIK, T_POTRDI_R],
  },
  {
    kljuc: 'nabava.placila', naslov: 'Plačila', za: 'vsi',
    ujema: (pot) => pot === '/placila',
    opis: 'Priprava in evidenca plačil prejetih računov (SEPA paket, UPN QR).',
    postopki: [P_PLACILA, P_SEPA],
    tezave: [T_LIKV],
  },
  {
    kljuc: 'nabava.sifranti', naslov: 'Šifranti', za: 'vsi',
    ujema: (pot) => pot === '/sifranti',
    opis: 'Partnerji in drugi šifranti podjetja.',
    postopki: [P_SIFRANTI, P_SIFRANTI_R],
  },
  {
    kljuc: 'nabava.nastavitve', naslov: 'Nastavitve Nabave', za: 'racunovodja',
    ujema: (pot) => pot === '/nastavitve',
    opis: 'Nastavitve za prejete račune.',
    postopki: [P_NASTAVITVE_R],
  },
  {
    kljuc: 'nabava.prenos', naslov: 'Prenos', za: 'racunovodja',
    ujema: (pot) => pot === '/prenosi',
    opis: 'Prenos prejetih dokumentov v računovodski program kot XML (VOD ali KN-21).',
    postopki: [P_PRENOS_R],
  },
];
